import { SovosBox, SovosIconButton, SovosTypography } from "mosaic-react";
import { Close } from "mosaic-react-icons";
import React, { useEffect, useRef } from "react";

const focusableSelector = [
    "button:not([disabled])",
    "a[href]",
    "input:not([disabled])",
    "select:not([disabled])",
    "textarea:not([disabled])",
    "[tabindex]:not([tabindex='-1'])",
].join(",");

const ChooseEntityDialogFrame = ({ children, disableClose, hasAi, i18n, onClose }) => {
    const dialogRef = useRef(null);
    const returnFocusRef = useRef(document.activeElement);

    useEffect(() => {
        const returnFocus = returnFocusRef.current;
        const dialog = dialogRef.current;
        const firstFocusable = dialog?.querySelector(focusableSelector);
        (firstFocusable || dialog)?.focus();
        return () => returnFocus?.focus();
    }, []);

    const close = () => {
        if (disableClose) return;
        returnFocusRef.current?.focus();
        onClose();
    };

    const handleKeyDown = (event) => {
        if (event.key === "Escape") {
            close();
            return;
        }
        if (event.key !== "Tab") return;
        const focusable = [...(dialogRef.current?.querySelectorAll(focusableSelector) || [])];
        if (!focusable.length) {
            event.preventDefault();
            dialogRef.current?.focus();
            return;
        }
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (
            event.shiftKey &&
            (document.activeElement === first || document.activeElement === dialogRef.current)
        ) {
            event.preventDefault();
            last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    return (
        <SovosBox
            aria-labelledby="choose-entity-title"
            aria-modal="true"
            onKeyDown={handleKeyDown}
            ref={dialogRef}
            role="dialog"
            tabIndex={-1}
            sx={{
                bgcolor: "background.paper",
                borderRadius: 2,
                boxShadow: 24,
                maxWidth: "90vw",
                p: 3,
                position: "relative",
                width: 480,
            }}
        >
            <SovosBox sx={{ position: "absolute", right: 8, top: 8 }}>
                <SovosIconButton
                    aria-label={i18n`Close`}
                    autoFocus={!disableClose}
                    disabled={disableClose}
                    onClick={close}
                    size="small"
                    tooltipText={i18n`Close`}
                >
                    <Close fontSize="small" />
                </SovosIconButton>
            </SovosBox>
            <SovosTypography fontWeight={600} id="choose-entity-title" variant="h6">
                {hasAi
                    ? i18n`Hey, Sovi is helping with your data import`
                    : i18n`Start column mapping by choosing entity`}
            </SovosTypography>
            <SovosTypography
                color="text.secondary"
                sx={{ display: "block", mb: 3 }}
                variant="body2"
            >
                {i18n`Choose which country, legal structure, and entity this data should map to.`}
            </SovosTypography>
            {children}
        </SovosBox>
    );
};

export default ChooseEntityDialogFrame;
